import { useCallback, useEffect, useState } from 'react'
import {
  getConsent,
  setConsent,
  clearConsent,
  onOpenPreferencesRequest,
} from './consentManager'
import {
  syncConsentToBackend,
  syncRevokeToBackend,
  type ConsentBackendSource,
} from './consentBackend'

/**
 * LGPD Block B (UI) + Block C (backend mirror) — consent banner + preferences.
 *
 * Mounted once at the app root so it covers every surface (login, invite
 * acceptance and the authenticated workspace). Behavior:
 *
 *   - No valid stored decision ⇒ the bottom banner shows. Analytics and
 *     marketing remain OFF until the user explicitly opts in.
 *   - "Aceitar todos" and "Rejeitar todos" carry EQUAL visual weight (same
 *     component classes) — rejecting is exactly as easy as accepting.
 *   - "Gerenciar preferências" opens per-category toggles. Essential is
 *     always on and cannot be unchecked.
 *   - ``openConsentPreferences()`` (legal footer / settings) reopens the
 *     dialog at any time so a decision can be revised or revoked.
 *
 * The local record (consentManager) remains the single source of truth for
 * immediate UI gating. After each decision we ALSO send a best-effort,
 * fire-and-forget consent event to the backend (Block C) for LGPD evidence —
 * a backend failure never breaks the flow and loads no scripts.
 */

type View = 'none' | 'banner' | 'preferences'

const PRIVACY_POLICY_HREF = '/legal/privacidade.html'

/** Equal-weight styling shared by ALL consent action buttons (no dark patterns). */
const ACTION_BUTTON_CLASS =
  'rounded-sm border border-rule px-3 py-1.5 text-[12px] font-medium text-text transition-colors duration-150 hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet'

export function ConsentBanner() {
  const [view, setView] = useState<View>(() =>
    getConsent() === null ? 'banner' : 'none',
  )
  const [draftAnalytics, setDraftAnalytics] = useState(false)
  const [draftMarketing, setDraftMarketing] = useState(false)
  // Whether a stored decision exists when the dialog opens — gates the
  // "Revogar consentimento" control (nothing to revoke before a first choice).
  const [hasDecision, setHasDecision] = useState(false)

  const openPreferences = useCallback(() => {
    const current = getConsent()
    setDraftAnalytics(current?.categories.analytics ?? false)
    setDraftMarketing(current?.categories.marketing ?? false)
    setHasDecision(current !== null)
    setView('preferences')
  }, [])

  // Reopen requests from the legal footer / settings ("Preferências de
  // privacidade") arrive as window events — see consentManager.
  useEffect(() => onOpenPreferencesRequest(openPreferences), [openPreferences])

  const closeAfterDecision = useCallback(() => setView('none'), [])

  const dismissPreferences = useCallback(() => {
    // Without a stored decision the banner must come back; closing the
    // dialog is not consent.
    setView(getConsent() === null ? 'banner' : 'none')
  }, [])

  useEffect(() => {
    if (view !== 'preferences') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissPreferences()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [view, dismissPreferences])

  const acceptAll = useCallback(
    (source: ConsentBackendSource) => {
      const record = setConsent({ analytics: true, marketing: true }, 'accept_all')
      syncConsentToBackend(record, source)
      closeAfterDecision()
    },
    [closeAfterDecision],
  )

  const rejectAll = useCallback(
    (source: ConsentBackendSource) => {
      const record = setConsent(
        { analytics: false, marketing: false },
        'reject_all',
      )
      syncConsentToBackend(record, source)
      closeAfterDecision()
    },
    [closeAfterDecision],
  )

  const savePreferences = useCallback(() => {
    const record = setConsent(
      { analytics: draftAnalytics, marketing: draftMarketing },
      'save_preferences',
    )
    syncConsentToBackend(record, 'preferences')
    closeAfterDecision()
  }, [draftAnalytics, draftMarketing, closeAfterDecision])

  const revokeConsent = useCallback(() => {
    // Best-effort backend revoke event (append-only; never deletes), then drop
    // the local record so gating returns to "no consent" and the banner shows.
    syncRevokeToBackend('preferences')
    clearConsent()
    setView('banner')
  }, [])

  if (view === 'none') return null

  if (view === 'banner') {
    return (
      <div
        role="region"
        aria-label="Aviso de privacidade e cookies"
        data-testid="consent-banner"
        className="fixed inset-x-0 bottom-0 z-[80] border-t border-hairline bg-panel px-4 py-3 shadow-[0_-8px_24px_rgba(0,0,0,0.35)]"
      >
        <div className="mx-auto flex max-w-3xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[12px] leading-relaxed text-text-soft">
            Usamos cookies essenciais para autenticação e segurança. Cookies de
            análise e de marketing só são usados com o seu consentimento — por
            padrão, ficam desativados.{' '}
            <a
              href={PRIVACY_POLICY_HREF}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-text"
            >
              Política de Privacidade
            </a>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="consent-manage"
              className={ACTION_BUTTON_CLASS}
              onClick={openPreferences}
            >
              Gerenciar preferências
            </button>
            <button
              type="button"
              data-testid="consent-reject-all"
              className={ACTION_BUTTON_CLASS}
              onClick={() => rejectAll('banner')}
            >
              Rejeitar todos
            </button>
            <button
              type="button"
              data-testid="consent-accept-all"
              className={ACTION_BUTTON_CLASS}
              onClick={() => acceptAll('banner')}
            >
              Aceitar todos
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-preferences-title"
      data-testid="consent-preferences"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4"
      onClick={dismissPreferences}
    >
      <div
        className="w-full max-w-md rounded border border-hairline bg-panel p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="consent-preferences-title" className="text-[15px] font-semibold text-text">
          Preferências de privacidade
        </h2>
        <p className="mt-1 text-[12px] leading-relaxed text-text-soft">
          Escolha quais categorias de cookies e tecnologias semelhantes podemos
          usar. Você pode alterar essas preferências a qualquer momento.{' '}
          <a
            href={PRIVACY_POLICY_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-text"
          >
            Política de Privacidade
          </a>
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <label className="flex items-start gap-3 rounded-sm border border-rule p-3">
            <input
              type="checkbox"
              checked
              disabled
              aria-label="Essenciais (sempre ativos)"
              className="mt-0.5"
            />
            <span>
              <span className="block text-[13px] font-medium text-text">
                Essenciais{' '}
                <span className="text-[11px] font-normal text-text-dim">
                  (sempre ativos)
                </span>
              </span>
              <span className="block text-[12px] leading-relaxed text-text-soft">
                Necessários para login, sessão, segurança e proteção CSRF. Não
                podem ser desativados.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-sm border border-rule p-3">
            <input
              type="checkbox"
              data-testid="consent-toggle-analytics"
              checked={draftAnalytics}
              onChange={(e) => setDraftAnalytics(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="block text-[13px] font-medium text-text">Análise</span>
              <span className="block text-[12px] leading-relaxed text-text-soft">
                Nos ajudam a entender o uso do produto (ex.: análise de
                audiência). Desativados por padrão.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-sm border border-rule p-3">
            <input
              type="checkbox"
              data-testid="consent-toggle-marketing"
              checked={draftMarketing}
              onChange={(e) => setDraftMarketing(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="block text-[13px] font-medium text-text">Marketing</span>
              <span className="block text-[12px] leading-relaxed text-text-soft">
                Usados para medir e personalizar campanhas. Desativados por
                padrão.
              </span>
            </span>
          </label>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
          {hasDecision ? (
            <button
              type="button"
              data-testid="consent-revoke"
              className="text-[12px] text-text-dim underline underline-offset-2 transition-colors duration-150 hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet"
              onClick={revokeConsent}
            >
              Revogar consentimento
            </button>
          ) : (
            <span />
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="consent-preferences-reject-all"
              className={ACTION_BUTTON_CLASS}
              onClick={() => rejectAll('preferences')}
            >
              Rejeitar todos
            </button>
            <button
              type="button"
              data-testid="consent-preferences-accept-all"
              className={ACTION_BUTTON_CLASS}
              onClick={() => acceptAll('preferences')}
            >
              Aceitar todos
            </button>
            <button
              type="button"
              data-testid="consent-save"
              className={ACTION_BUTTON_CLASS}
              onClick={savePreferences}
            >
              Salvar preferências
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
